/**
 * The few pieces of the workbench's diagram tree the dated roadmap needs, so the console can ground
 * and draw the Gantt without the whole Diagrammer (`shared/diagrams/`, about 8,700 lines, with its
 * layout engine and hand-drawn pen). Taken from, in mju-ai-workbench:
 *
 *   shared/diagrams/spec.ts           DIAGRAM_TONES, ItemStatus, TimelineLane, TimelineItem, the timeline spec, DiagramNote
 *   shared/diagrams/repair.ts         comparePhase; and the timeline repair pass, cut to what a grounded Gantt can meet
 *   shared/diagrams/ground/roadmap.ts RoadmapRowLike, GroundRoadmapOptions, statusOf, laneOf
 *
 * Pure: no DOM. The forecast itself is ./roadmapGantt.ts, the picture ./GanttChart.tsx.
 */
import { roadmapAreaPaths, type RoadmapItem, seriesOf, SERIES_TITLES } from '@aioe/shared/roadmap/model';

// ── spec.ts ─────────────────────────────────────────────────────────────

export const DIAGRAM_TONES = ['teal', 'violet', 'blue', 'amber', 'rose', 'green', 'slate'] as const;
export type DiagramTone = (typeof DIAGRAM_TONES)[number];

/** Roadmap statuses, as `shared/roadmap/model.ts` has them. */
export const ITEM_STATUSES = ['not-started', 'in-progress', 'done', 'deferred', 'wont-do'] as const;
export type ItemStatus = (typeof ITEM_STATUSES)[number];

export interface TimelineLane {
  id: string;
  label: string;
  tone?: DiagramTone;
}

export interface TimelineItem {
  id: string;
  title: string;
  lane: string;
  /** The roadmap's phase cell. */
  phase?: string;
  status?: ItemStatus;
  /** ISO date (YYYY-MM-DD). With `end`, a bar on a date scale. */
  start?: string;
  end?: string;
  /** A month (YYYY-MM), a quarter (YYYY-Qn) or a day: the roadmap's optional target column. */
  target?: string;
  /** Ids this one waits on. */
  dependsOn?: string[];
  milestone?: boolean;
  /** The roadmap row it stands for. */
  ref?: string;
  /** A date forecast rather than one a person set: drawn hatched, with the word. */
  forecast?: boolean;
  /** For an item with a target: the day the recent pace says it will really be done, when that is after the target. */
  forecastEnd?: string;
  /** This card stands for N items not drawn ("4 more"). */
  more?: number;
}

/** The dated timeline spec (the workbench's `TimelineDiagram` on its date scale), without the Diagrammer's provenance. */
export interface TimelineDiagram {
  kind: 'timeline';
  scale: 'date';
  meta: { title: string; summary: string };
  notes?: string;
  lanes: TimelineLane[];
  items: TimelineItem[];
  /** ISO date of "today". */
  now?: string;
  criticalPath?: string[];
}

/** A note from the repair pass, in plain words. */
export interface DiagramNote {
  rule: string;
  severity: 'info' | 'warning';
  message: string;
}

export interface RepairResult<T> {
  spec: T;
  notes: DiagramNote[];
}

// ── repair.ts ───────────────────────────────────────────────────────────

export function comparePhase(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (a.trim() !== '' && b.trim() !== '' && Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return a.localeCompare(b);
}

/**
 * The timeline repair pass, as far as a grounded Gantt can need it: dates the right way round,
 * "waits on" only to items on the picture, no empty lanes, the critical path only through items drawn.
 */
export function repairSpec(spec: TimelineDiagram): RepairResult<TimelineDiagram> {
  const notes: DiagramNote[] = [];
  const say = (rule: string, severity: DiagramNote['severity'], message: string) => notes.push({ rule, severity, message });
  const ids = new Set(spec.items.map((i) => i.id));
  let dropped = 0;
  const items = spec.items.map((raw) => {
    let item = raw;
    if (item.start && item.end && item.end < item.start) {
      say('dates', 'warning', `Swapped the start and end of ${item.id}: it ended before it began.`);
      item = { ...item, start: item.end, end: item.start };
    }
    if (!item.dependsOn) return item;
    const deps = [...new Set(item.dependsOn.filter((d) => ids.has(d) && d !== item.id))];
    dropped += item.dependsOn.length - deps.length;
    if (deps.length) return { ...item, dependsOn: deps };
    const { dependsOn: _drop, ...rest } = item;
    return rest;
  });
  if (dropped) say('depends', 'info', `Dropped ${dropped} "waits on" reference${dropped === 1 ? '' : 's'} to items not on the picture.`);
  const used = new Set(items.map((i) => i.lane));
  const lanes = spec.lanes.filter((l) => used.has(l.id));
  const criticalPath = spec.criticalPath?.filter((id) => ids.has(id));
  return { spec: { ...spec, lanes, items, ...(criticalPath ? { criticalPath } : {}) }, notes };
}

// ── ground/roadmap.ts ───────────────────────────────────────────────────

export type RoadmapRowLike = Pick<RoadmapItem, 'id' | 'title' | 'area' | 'phase' | 'status'> & {
  dependsOn?: readonly string[];
  target?: string;
  /** How big it is, in estimated points. The dated roadmap's pace goes by these when most items have one. */
  estimate?: number;
};

export interface GroundRoadmapOptions {
  /** Lanes by id series (default) or by top-level area. */
  laneBy?: 'series' | 'area';
  now?: string;
  title?: string;
  /** The level it is drawn at (default: one repository). */
  scope?: 'repository' | 'project';
}

const STATUSES: readonly string[] = ['not-started', 'in-progress', 'done', 'deferred', 'wont-do'];
export const statusOf = (s: string): ItemStatus => (STATUSES.includes(s) ? (s as ItemStatus) : 'not-started');

export function laneOf(row: RoadmapRowLike, laneBy: 'series' | 'area'): { id: string; label: string } {
  if (laneBy === 'area') {
    const first = roadmapAreaPaths(row.area)[0] ?? '';
    const top = first.split('/')[0] || '(all)';
    return { id: `area-${top.replace(/[^A-Za-z0-9._-]+/g, '') || 'all'}`, label: top === '(root)' || top === '(all)' ? 'Whole repository' : top };
  }
  const series = seriesOf(row.id);
  return { id: `series-${series}`, label: SERIES_TITLES[series] ?? `${series} series` };
}
